// WS-2 (docs/plans/AUTH_SESSION_HARDENING.md — CSRF transition): frontend half
// of the double-submit-cookie CSRF defence.
//
// The backend CSRF middleware (internal/api/middleware/csrf.go) seeds a
// JS-readable (NOT HttpOnly, by design) `__csrf_token` cookie on every
// safe-method response. For state-changing requests, the SPA must echo that same
// token back in the `X-CSRF-Token` header; the server compares header == cookie
// (constant-time) and rejects on mismatch. A cross-site attacker can force a
// request but cannot READ the victim's cookie to set the matching header, so the
// forged request fails the comparison.
//
// This is dormant today: the middleware runs in SHADOW mode (VHARTA_CSRF_ENFORCE
// off) and still exempts Bearer-authenticated requests, and the SPA still sends
// Bearer. Sending the header now is harmless and readies the client for the
// enforce flip that lands with the cookie-only cutover. Only UNSAFE (mutating)
// methods carry the header — safe methods never need it and adding it there would
// just cause needless CORS preflights cross-origin.

export const CSRF_HEADER = 'X-CSRF-Token'

// WS-9: the CSRF cookie is __Host--prefixed on https (the browser then enforces
// Secure+Path=/+no-Domain, so a sibling subdomain can't plant a forged
// double-submit token) and the bare name on http/dev (prefixed cookies require
// Secure). Read prefixed-first — mirrors the backend authcookie.ReadAny pattern.
const CSRF_COOKIE_NAMES = ['__Host-__csrf_token', '__csrf_token'] as const
// Precompiled once (module scope) — readCsrfToken is on the hot path of every
// mutating request. The names are fixed literals with no regex metacharacters,
// so no escaping is needed; if CSRF_COOKIE_NAMES ever gains a dynamic name,
// escape it here. The `(?:^|;\s*)` anchor prevents partial matches.
const CSRF_COOKIE_PATTERNS = CSRF_COOKIE_NAMES.map(
  (name) => new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'),
)

// Safe (non-mutating) HTTP methods — mirrors the backend's SkipMethods set.
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS', 'TRACE'])

/**
 * readCsrfToken returns the current CSRF-token cookie value (prefixed name first,
 * bare fallback), or null if not seeded yet. Reads document.cookie directly — the
 * cookie is intentionally non-HttpOnly so the double-submit pattern can work.
 */
export function readCsrfToken(): string | null {
  const cookie = document.cookie
  for (const re of CSRF_COOKIE_PATTERNS) {
    // Anchored so we never partially match a differently-named cookie
    // (e.g. bare `__csrf_token=` inside `__Host-__csrf_token=`).
    const match = cookie.match(re)
    if (!match) continue
    // The server (Go http.SetCookie) never percent-encodes the base64url token,
    // so decode is normally a no-op — but guard against a malformed value (stray
    // '%', a browser extension) since this is on the hot path of every mutating
    // request once CSRF enforce is on. Fail safe: return null rather than throw.
    try {
      return decodeURIComponent(match[1])
    } catch {
      return null
    }
  }
  return null
}

/** isUnsafeMethod reports whether a request method requires CSRF protection. */
export function isUnsafeMethod(method?: string): boolean {
  return !SAFE_METHODS.has((method ?? 'GET').toUpperCase())
}

/**
 * csrfHeader returns the `{ 'X-CSRF-Token': <token> }` object to merge into an
 * unsafe request's headers, or an empty object when the method is safe or no
 * token cookie is present. Callers spread the result into their header map.
 */
export function csrfHeader(method?: string): Record<string, string> {
  if (!isUnsafeMethod(method)) return {}
  const token = readCsrfToken()
  return token ? { [CSRF_HEADER]: token } : {}
}
